import { useEffect, useMemo, useState, Dispatch, SetStateAction, useCallback } from "react";

import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import pickBy from "lodash/pickBy";
import { parse as qsParse, stringify as qsStringify } from "qs";
import { useLocation, useNavigate } from "react-router-dom";

import { QueryParams, queryParamsProps } from "../../RVGridTypes";

type QueryParamsState = {
    baseParams: Partial<QueryParams>;
    queryParams: Partial<QueryParams>;
    uiQueryParams: Partial<QueryParams>;
    setUiQueryParams: Dispatch<SetStateAction<Partial<QueryParams>>>;
};

export const defaultQueryParams: Partial<QueryParams> = {
    page: 1,
    pageSize: 25,
};

function isNumber(n: any): n is number | string {
    return !isNaN(parseFloat(n)) && !isNaN(n - 0);
}

export function getURLQueryParams(search: string): { query: Partial<QueryParams>; extra: Record<string, any> } {
    // we set a limit to the amount of items within an array field that will be parsed without flattening into an object
    const allParams = qsParse(search?.replace(/^\?/, ""), { arrayLimit: 250 }); // strip off any leading `?`

    const queryParams = Object.entries(allParams).reduce(
        ({ query, extra }, [key, val]) => {
            if (["page", "pageSize"].includes(key)) {
                // if we know we're expecting a strict number here, use the defaults if we don't a number
                query[key] = isNumber(val) ? Number(val) : defaultQueryParams[key as keyof QueryParams];
            } else if (queryParamsProps.includes(key)) {
                query[key] = isNumber(val) ? parseInt(val) : val;
            } else if (key != "newTab") {
                extra[key] = val;
            }
            return { query, extra };
        },
        {
            query: {} as Record<string, any>,
            extra: {} as Record<string, any>,
        }
    );
    return queryParams;
}

function areQueryParamsEqual(left: string, right: string) {
    return isEqual(qsParse(left.replace(/^\?/, "")), qsParse(right.replace(/^\?/, "")));
}

type InitialQueryParams = Partial<QueryParams>;

/**
 * Keeps provided params in sync between URL, API-ready query params (0-indexed), and a UI representation (1-indexed).
 *
 * @param initialQueryOption - Optional defaults (page size, filters, etc.) that seed both the URL and local state.
 * @returns Base/default params plus the current query/ui params and a setter that updates both state and URL.
 */
export const useQueryParamsV6 = (initialQueryOption?: InitialQueryParams): QueryParamsState => {
    const navigate = useNavigate();
    const location = useLocation();

    const baseParams = useMemo(
        () => ({
            ...defaultQueryParams,
            ...initialQueryOption,
        }),
        [initialQueryOption]
    );

    const [uiQueryParams, internalSetUiQueryParams] = useState<Partial<QueryParams>>({
        ...baseParams,
        ...getURLQueryParams(location.search).query,
    });

    const queryParams = useMemo(
        () => ({
            ...initialQueryOption,
            ...uiQueryParams,
            page: isNumber(uiQueryParams.page) ? Number(uiQueryParams.page) - 1 : 0,
        }),
        [uiQueryParams, initialQueryOption]
    );

    // Custom setter: updates state and URL atomically
    const setUiQueryParams = useCallback<Dispatch<SetStateAction<Partial<QueryParams>>>>(
        (updater) => {
            internalSetUiQueryParams((prev) => {
                const next = typeof updater === "function" ? updater(prev) : updater;
                // Build new search string
                const newSearchObject = {
                    ...initialQueryOption,
                    ...getURLQueryParams(location.search).extra,
                    ...pickBy(next),
                };
                if (isNumber(newSearchObject.page)) {
                    newSearchObject.page = Number(newSearchObject.page);
                }
                const newSearch = qsStringify(newSearchObject);
                if (!areQueryParamsEqual(newSearch, location.search)) {
                    navigate({ search: newSearch }, { replace: true });
                }
                return next;
            });
        },
        [initialQueryOption, location.search, navigate]
    );

    useEffect(() => {
        const { query } = getURLQueryParams(location.search);
        if (!isEqual(query, pickBy(uiQueryParams))) {
            // in order to prevent reverting query params to empty when a page loads without query params (or if they are cleared), we default to base params
            if (isEmpty(query)) {
                setUiQueryParams(baseParams);
                return;
            }
            internalSetUiQueryParams(query);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only care about the URL changing
    }, [location.search]);

    return {
        baseParams,
        queryParams,
        uiQueryParams,
        setUiQueryParams,
    };
};
